"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AiChartLegendContent,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  resolveAiLegendLayout,
} from "./ai-chart";
import {
  resolveBarComparisonSizing,
  resolveBarSeparatorStyle,
  shouldShowBarComparisonLegend,
} from "./bar-presentation";
import { renderSquareZeroSafeBarShape } from "./bar-shape";
import {
  analyzeCategoricalAxisLabels,
  formatCategoryTickLabel,
  resolveCategoryAxisWidth,
  resolveRequestedChartHeight,
} from "./categorical-chart-core";
import {
  coerceNumber,
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  EMPTY_VIZ_SERIES,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipMetricValue,
  isRecord,
  normalizeVizExportFormats,
  resolveEffectiveXAxisTickDensity,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizPaletteMode,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  resolveVizTooltipMode,
  resolveXAxisTickDensityConfig,
  resolveXAxisTickLayout,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizReferenceLine,
  type VizTooltipMode,
  type VizXAxisTickDensity,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  formatVizAxisDateLabel,
  formatVizTooltipDomainLabel,
} from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveCategoryLabel, resolveDisplayLabel } from "./label-resolver";
import {
  areStringArraysEqual,
  reconcileVisibleSeriesKeys,
  resolveInitialVisibleSeriesKeys,
  toggleVisibleSeriesKeys,
} from "./series-visibility";
import {
  VizActionBar,
  VizExportMenu,
  VizSeriesMenu,
  type VizSortDirection,
  VizSortMenu,
} from "./viz-actions";

export type BarComparisonProps = BaseComponentProps<{
  dataPath: string;
  categoryKey: string;
  series?: Array<{ key: string; label?: string | null; color?: string | null }>;
  layout?: "vertical" | "horizontal" | null;
  chartHeight?: number | null;
  stacking?: "grouped" | "stacked" | null;
  sort?: "desc" | "asc" | "none" | null;
  yAxisMode?: "auto" | "single" | "dual" | null;
  seriesAxisMap?: Record<string, "left" | "right"> | null;
  categoryLabel?: string | null;
  seriesLabels?: Record<string, string> | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
  xAxisTicks?: { density?: VizXAxisTickDensity | null } | null;
  referenceLines?: VizReferenceLine[] | null;
  allowSortControl?: boolean | null;
  allowSeriesToggle?: boolean | null;
  defaultVisibleSeriesKeys?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
}>;

const MIN_HORIZONTAL_HEIGHT = 300;
const DEFAULT_HORIZONTAL_HEIGHT = 320;
const MIN_VERTICAL_HEIGHT = 300;
const MAX_VERTICAL_HEIGHT = 560;
const MAX_HORIZONTAL_HEIGHT = 440;
const ROW_SLOT_HEIGHT = 30;
const VERTICAL_PADDING = 48;
const MIN_HORIZONTAL_PLOT_WIDTH = 720;
const MIN_VERTICAL_PLOT_WIDTH = 720;
type BarComparisonYAxisMode = "auto" | "single" | "dual";
type BarComparisonYAxisSide = "left" | "right";

function resolveYAxisMode(value: unknown): BarComparisonYAxisMode {
  if (value === "single" || value === "dual") return value;
  return "auto";
}

/**
 * カテゴリ単位で複数系列を比較する棒グラフを描画する。
 *
 * @remarks
 * dual-axis や series toggle の複雑な挙動は維持したまま、style preset のみを
 * AI 側 props で切り替えて可読性とモダンさを改善する。
 */
export function BarComparison({ props }: BarComparisonProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    categoryKey,
    series,
    layout,
    chartHeight: requestedChartHeight,
    stacking,
    sort,
    yAxisMode,
    seriesAxisMap,
    categoryLabel,
    seriesLabels,
    axisPreset,
    tooltipMode,
    paletteMode,
    xAxisTicks,
    referenceLines,
    allowSortControl,
    allowSeriesToggle,
    defaultVisibleSeriesKeys,
    allowExport,
    exportFormats,
  } = props;
  const resolvedTooltipMode = resolveVizTooltipMode(tooltipMode);
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);
  const chartLayout = layout ?? "vertical";
  const seriesList = Array.isArray(series) ? series : EMPTY_VIZ_SERIES;

  const [interactiveSort, setInteractiveSort] = useState<VizSortDirection>(
    sort ?? "desc",
  );

  const allSeriesKeys = useMemo(
    () => seriesList.map((entry) => entry.key),
    [seriesList],
  );

  const [visibleSeriesKeys, setVisibleSeriesKeys] = useState<string[]>(() =>
    resolveInitialVisibleSeriesKeys({
      allSeriesKeys,
      defaultVisibleSeriesKeys,
    }),
  );

  useEffect(() => {
    setVisibleSeriesKeys((current) => {
      const next = reconcileVisibleSeriesKeys({ current, allSeriesKeys });
      if (areStringArraysEqual(next, current)) {
        return current;
      }
      return next;
    });
  }, [allSeriesKeys]);

  const toggleSeriesVisibility = useCallback(
    (key: string, checked?: boolean) => {
      setVisibleSeriesKeys((current) => {
        const updated = toggleVisibleSeriesKeys({
          current,
          allSeriesKeys,
          key,
          checked,
        });
        return areStringArraysEqual(updated, current) ? current : updated;
      });
    },
    [allSeriesKeys],
  );

  const visibleSeriesKeySet = useMemo(
    () => new Set(visibleSeriesKeys),
    [visibleSeriesKeys],
  );

  const effectiveSeriesList = useMemo(() => {
    return seriesList.filter((entry) => visibleSeriesKeySet.has(entry.key));
  }, [seriesList, visibleSeriesKeySet]);

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );

  const sortedData = useMemo(() => {
    const rows = data;
    if (interactiveSort === "none") return rows;

    return [...rows].sort((a, b) => {
      const aTotal = effectiveSeriesList.reduce(
        (sum, entry) => sum + coerceNumber(a[entry.key]),
        0,
      );
      const bTotal = effectiveSeriesList.reduce(
        (sum, entry) => sum + coerceNumber(b[entry.key]),
        0,
      );
      return interactiveSort === "asc" ? aTotal - bTotal : bTotal - aTotal;
    });
  }, [data, effectiveSeriesList, interactiveSort]);

  const categoryFieldMeta = metaByName.get(categoryKey);
  const formatCategoryAxisTick = useCallback(
    (value: unknown) => {
      const resolved = resolveCategoryLabel({
        value,
        fieldMeta: categoryFieldMeta,
        i18n,
      });
      return formatCategoryTickLabel({
        label: formatVizAxisDateLabel(resolved, i18n),
      });
    },
    [categoryFieldMeta, i18n],
  );

  const formatCategoryTooltipLabel = useCallback(
    (value: unknown) => {
      const resolved = resolveCategoryLabel({
        value,
        fieldMeta: categoryFieldMeta,
        i18n,
      });
      const formatted = formatVizTooltipDomainLabel(resolved, i18n);
      return categoryLabel ? `${categoryLabel}: ${formatted}` : formatted;
    },
    [categoryFieldMeta, categoryLabel, i18n],
  );

  const percentKeys = useMemo(() => {
    return effectiveSeriesList
      .map((entry) => entry.key)
      .filter((key) => isPercentFieldKey(key, metaByName));
  }, [effectiveSeriesList, metaByName]);

  const percentKeySet = useMemo(() => new Set(percentKeys), [percentKeys]);
  const formatTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: percentKeySet.has(key),
      }),
    [percentKeySet],
  );

  const requestedYAxisMode = useMemo(
    () => resolveYAxisMode(yAxisMode),
    [yAxisMode],
  );
  const requestedSeriesAxisMap = useMemo(() => {
    if (!seriesAxisMap) return {} as Record<string, BarComparisonYAxisSide>;
    const normalized: Record<string, BarComparisonYAxisSide> = {};
    for (const [key, value] of Object.entries(seriesAxisMap)) {
      if (value === "left" || value === "right") {
        normalized[key] = value;
      }
    }
    return normalized;
  }, [seriesAxisMap]);

  const autoDualAxis = useMemo(() => {
    if (effectiveSeriesList.length < 2) return false;
    if (
      percentKeys.length === 0 ||
      percentKeys.length === effectiveSeriesList.length
    ) {
      return false;
    }
    return true;
  }, [effectiveSeriesList.length, percentKeys.length]);

  const useDualAxis = useMemo(() => {
    if (requestedYAxisMode === "dual") return true;
    if (requestedYAxisMode === "single") return false;
    return autoDualAxis;
  }, [autoDualAxis, requestedYAxisMode]);

  const resolveSeriesAxisSide = useCallback(
    (seriesKey: string): BarComparisonYAxisSide => {
      if (requestedYAxisMode === "dual") {
        const requested = requestedSeriesAxisMap[seriesKey];
        if (requested === "left" || requested === "right") {
          return requested;
        }
      }
      return percentKeySet.has(seriesKey) ? "right" : "left";
    },
    [percentKeySet, requestedSeriesAxisMap, requestedYAxisMode],
  );

  const rightAxisSeriesKeys = useMemo(() => {
    if (!useDualAxis) return [];
    return effectiveSeriesList
      .map((entry) => entry.key)
      .filter((key) => resolveSeriesAxisSide(key) === "right");
  }, [effectiveSeriesList, resolveSeriesAxisSide, useDualAxis]);

  const shouldFormatRightAxisToPercent = useMemo(() => {
    if (rightAxisSeriesKeys.length === 0) return false;
    return rightAxisSeriesKeys.every((key) => percentKeySet.has(key));
  }, [percentKeySet, rightAxisSeriesKeys]);

  const shouldFormatAxisToPercent = useMemo(() => {
    if (effectiveSeriesList.length === 0) return false;
    if (percentKeys.length === 0) return false;
    return percentKeys.length === effectiveSeriesList.length;
  }, [effectiveSeriesList.length, percentKeys.length]);

  const rowCount = sortedData.length;
  const isVertical = chartLayout === "vertical";
  const resolvedReferenceLines = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );
  const resolvedStacking = useDualAxis ? "grouped" : stacking;
  const stackId = resolvedStacking === "stacked" ? "stack" : undefined;
  const groupedSeriesCount =
    resolvedStacking === "stacked"
      ? 1
      : Math.max(1, effectiveSeriesList.length);

  const verticalRowSlotHeight = useMemo(() => {
    if (!isVertical) return ROW_SLOT_HEIGHT;
    const groupedTarget =
      groupedSeriesCount * 14 + (groupedSeriesCount - 1) * 2 + 10;
    return Math.max(ROW_SLOT_HEIGHT, groupedTarget);
  }, [groupedSeriesCount, isVertical]);

  const verticalChartHeight =
    rowCount * verticalRowSlotHeight + VERTICAL_PADDING;
  const chartHeight = useMemo(() => {
    const requested = resolveRequestedChartHeight(requestedChartHeight);
    if (requested !== null) {
      if (isVertical) {
        return Math.max(
          MIN_VERTICAL_HEIGHT,
          Math.min(MAX_VERTICAL_HEIGHT, requested),
        );
      }
      return Math.max(
        MIN_HORIZONTAL_HEIGHT,
        Math.min(MAX_HORIZONTAL_HEIGHT, requested),
      );
    }
    if (!isVertical) return DEFAULT_HORIZONTAL_HEIGHT;
    return Math.max(
      MIN_VERTICAL_HEIGHT,
      Math.min(MAX_VERTICAL_HEIGHT, verticalChartHeight),
    );
  }, [isVertical, requestedChartHeight, verticalChartHeight]);

  const plotMinWidth = useMemo(() => {
    if (isVertical) return MIN_VERTICAL_PLOT_WIDTH;
    const perCategoryWidth =
      resolvedStacking === "stacked"
        ? 44
        : Math.max(54, groupedSeriesCount * 18 + 20);
    return Math.max(MIN_HORIZONTAL_PLOT_WIDTH, rowCount * perCategoryWidth);
  }, [groupedSeriesCount, isVertical, resolvedStacking, rowCount]);

  const yAxisInterval = useMemo(() => {
    if (!isVertical) return 0;
    const maxVisibleLabels = Math.max(
      1,
      Math.floor((chartHeight - VERTICAL_PADDING) / verticalRowSlotHeight),
    );
    if (rowCount <= maxVisibleLabels) return 0;
    return Math.ceil(rowCount / maxVisibleLabels) - 1;
  }, [chartHeight, isVertical, rowCount, verticalRowSlotHeight]);

  const yAxisWidth = useMemo(() => {
    return resolveCategoryAxisWidth({
      rows: sortedData,
      categoryKey,
      formatCategoryAxisTick,
    });
  }, [categoryKey, formatCategoryAxisTick, sortedData]);

  const chartConfig = useMemo(() => {
    return Object.fromEntries(
      seriesList.map((entry, index) => {
        const label = resolveDisplayLabel(
          seriesLabels?.[entry.key],
          entry.label,
          entry.key,
        );
        return [
          entry.key,
          {
            label,
            color: resolveVizSeriesColor({
              index,
              color: entry.color,
              paletteMode: resolvedPaletteMode,
            }),
          },
        ];
      }),
    );
  }, [resolvedPaletteMode, seriesList, seriesLabels]);

  const categoryAxisLabelAnalysis = useMemo(
    () =>
      analyzeCategoricalAxisLabels({
        rows: sortedData,
        categoryKey,
        formatCategoryAxisTick,
        isVertical,
      }),
    [categoryKey, formatCategoryAxisTick, isVertical, sortedData],
  );
  const rotateXAxis = categoryAxisLabelAnalysis.shouldRotate;
  const xAxisTickLayout = useMemo(() => {
    return resolveXAxisTickLayout({
      rotate: rotateXAxis,
      labels: categoryAxisLabelAnalysis.sampleLabels,
    });
  }, [categoryAxisLabelAnalysis.sampleLabels, rotateXAxis]);
  const xAxisTickDensityConfig = useMemo(() => {
    return resolveXAxisTickDensityConfig({
      density: resolveEffectiveXAxisTickDensity({
        density: xAxisTicks?.density,
        isMobile,
      }),
      rowCount,
      baseMinTickGap: xAxisTickLayout.minTickGap,
    });
  }, [isMobile, rowCount, xAxisTickLayout.minTickGap, xAxisTicks?.density]);
  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );

  const barSizing = useMemo(() => {
    return resolveBarComparisonSizing({
      chartHeight,
      rowCount,
      isVertical,
      grouped: resolvedStacking !== "stacked",
      seriesCount: effectiveSeriesList.length,
      verticalPadding: VERTICAL_PADDING,
    });
  }, [
    chartHeight,
    effectiveSeriesList.length,
    isVertical,
    resolvedStacking,
    rowCount,
  ]);
  const barSeparatorStyle = useMemo(
    () => resolveBarSeparatorStyle({ stacking: resolvedStacking }),
    [resolvedStacking],
  );

  const resolvedAllowSortControl = allowSortControl !== false;
  const resolvedAllowSeriesToggle = allowSeriesToggle !== false;
  const resolvedAllowExport = allowExport !== false;
  const resolvedExportFormats = normalizeVizExportFormats(
    exportFormats,
    DEFAULT_EXPORT_FORMATS,
  );

  const seriesMenuItems = useMemo(() => {
    return seriesList.map((entry) => ({
      key: entry.key,
      label: resolveDisplayLabel(
        seriesLabels?.[entry.key],
        entry.label,
        entry.key,
      ),
    }));
  }, [seriesList, seriesLabels]);
  const legendPayload = useMemo(() => {
    return seriesList.map((entry) => {
      const displayLabel = resolveDisplayLabel(
        chartConfig[entry.key]?.label,
        entry.label,
        entry.key,
      );
      return {
        dataKey: entry.key,
        value: displayLabel,
        color: chartConfig[entry.key]?.color ?? "var(--chart-1)",
      };
    });
  }, [chartConfig, seriesList]);
  const legendLayout = useMemo(
    () =>
      resolveAiLegendLayout({
        itemCount: legendPayload.length,
        isMobile,
      }),
    [isMobile, legendPayload.length],
  );
  const shouldShowLegend = useMemo(
    () => shouldShowBarComparisonLegend(legendPayload.length),
    [legendPayload.length],
  );
  const hasChartData = sortedData.length > 0 && effectiveSeriesList.length > 0;
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);
  return (
    <div ref={containerRef} className="w-full">
      <VizActionBar>
        {resolvedAllowSortControl ? (
          <VizSortMenu value={interactiveSort} onChange={setInteractiveSort} />
        ) : null}
        {resolvedAllowSeriesToggle ? (
          <VizSeriesMenu
            series={seriesMenuItems}
            visibleKeySet={visibleSeriesKeySet}
            onToggle={(key, checked) => {
              toggleSeriesVisibility(key, checked);
            }}
          />
        ) : null}
        {resolvedAllowExport ? (
          <VizExportMenu
            formats={resolvedExportFormats}
            disabled={!hasChartData}
            onCopyImage={handleCopyImage}
            onExport={async (format) => {
              if (format === "csv") {
                downloadRowsAsCsv({
                  rows: sortedData,
                  columns: [
                    categoryKey,
                    ...effectiveSeriesList.map((entry) => entry.key),
                  ],
                  baseName: "viz-bar-comparison",
                });
                return;
              }

              if (format === "json") {
                downloadRowsAsJson({
                  rows: sortedData,
                  columns: [
                    categoryKey,
                    ...effectiveSeriesList.map((entry) => entry.key),
                  ],
                  baseName: "viz-bar-comparison",
                });
                return;
              }

              if (format === "png") {
                await downloadVizAsPng({
                  element: containerRef.current,
                  baseName: "viz-bar-comparison",
                });
              }
            }}
          />
        ) : null}
      </VizActionBar>

      <div data-slot="bar-comparison-chart-shell" className="w-full">
        <div
          data-slot="bar-comparison-chart-scrollport"
          data-viz-capture-horizontal-scrollport="true"
          className="w-full min-w-0 overflow-x-auto pb-1"
        >
          <div
            data-slot="bar-comparison-plot"
            data-viz-capture-horizontal-content="true"
            className="overflow-hidden"
            style={plotMinWidth ? { minWidth: `${plotMinWidth}px` } : undefined}
          >
            <ChartContainer
              config={chartConfig}
              className="w-full aspect-auto rounded-[18px]"
              style={{ height: `${chartHeight}px` }}
            >
              <BarChart
                data={sortedData}
                layout={chartLayout}
                margin={{
                  top: useDualAxis && isVertical ? 24 : 16,
                  right: useDualAxis && !isVertical ? 64 : 16,
                  bottom: isVertical ? 10 : xAxisTickLayout.bottomMargin + 10,
                  left: 8,
                }}
                barCategoryGap={barSizing.barCategoryGap}
                barGap={barSizing.barGap}
              >
                <CartesianGrid
                  {...resolveVizCartesianGridProps({
                    axisStyle,
                    xAxisId: useDualAxis && isVertical ? "value" : undefined,
                    yAxisId: useDualAxis && !isVertical ? "left" : undefined,
                  })}
                />
                {isVertical ? (
                  <>
                    {useDualAxis ? (
                      <>
                        <XAxis
                          xAxisId="value"
                          type="number"
                          orientation="bottom"
                          tickFormatter={formatValueTick}
                          tickLine={resolveVizAxisTickLineProps()}
                          axisLine={resolveVizAxisLineProps()}
                          tickMargin={axisStyle.tickMargin}
                          tick={{ fontSize: axisStyle.tickFontSize }}
                        />
                        <XAxis
                          xAxisId="percent"
                          type="number"
                          orientation="top"
                          tickFormatter={
                            shouldFormatRightAxisToPercent
                              ? formatPercentTick
                              : formatValueTick
                          }
                          tickLine={resolveVizAxisTickLineProps()}
                          axisLine={resolveVizAxisLineProps()}
                          tickMargin={axisStyle.tickMargin}
                          tick={{ fontSize: axisStyle.tickFontSize }}
                        />
                      </>
                    ) : (
                      <XAxis
                        type="number"
                        tickFormatter={
                          shouldFormatAxisToPercent
                            ? formatPercentTick
                            : formatValueTick
                        }
                        tickLine={resolveVizAxisTickLineProps()}
                        axisLine={resolveVizAxisLineProps()}
                        tickMargin={axisStyle.tickMargin}
                        tick={{ fontSize: axisStyle.tickFontSize }}
                      />
                    )}
                    <YAxis
                      type="category"
                      dataKey={categoryKey}
                      width={yAxisWidth}
                      interval={yAxisInterval}
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickFormatter={formatCategoryAxisTick}
                      tickMargin={axisStyle.tickMargin}
                      tick={{ fontSize: axisStyle.tickFontSize }}
                    />
                  </>
                ) : (
                  <>
                    <XAxis
                      type="category"
                      dataKey={categoryKey}
                      tickLine={resolveVizAxisTickLineProps()}
                      axisLine={resolveVizAxisLineProps()}
                      tickFormatter={formatCategoryAxisTick}
                      tickMargin={Math.max(
                        xAxisTickLayout.tickMargin,
                        axisStyle.tickMargin,
                      )}
                      minTickGap={xAxisTickDensityConfig.minTickGap}
                      interval={xAxisTickDensityConfig.interval}
                      angle={xAxisTickLayout.angle}
                      textAnchor={xAxisTickLayout.textAnchor}
                      height={xAxisTickLayout.height}
                      tick={
                        rotateXAxis
                          ? {
                              dx: xAxisTickLayout.tickDx,
                              dy: xAxisTickLayout.tickDy,
                              fontSize: axisStyle.tickFontSize,
                            }
                          : { fontSize: axisStyle.tickFontSize }
                      }
                    />
                    {useDualAxis ? (
                      <>
                        <YAxis
                          yAxisId="left"
                          type="number"
                          tickFormatter={formatValueTick}
                          tickLine={resolveVizAxisTickLineProps()}
                          axisLine={resolveVizAxisLineProps()}
                          tickMargin={axisStyle.tickMargin}
                          width={axisStyle.yAxisWidth}
                          tick={{ fontSize: axisStyle.tickFontSize }}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          type="number"
                          tickFormatter={
                            shouldFormatRightAxisToPercent
                              ? formatPercentTick
                              : formatValueTick
                          }
                          tickLine={resolveVizAxisTickLineProps()}
                          axisLine={resolveVizAxisLineProps()}
                          tickMargin={axisStyle.tickMargin}
                          width={axisStyle.yAxisWidth}
                          tick={{ fontSize: axisStyle.tickFontSize }}
                        />
                      </>
                    ) : (
                      <YAxis
                        type="number"
                        tickFormatter={
                          shouldFormatAxisToPercent
                            ? formatPercentTick
                            : formatValueTick
                        }
                        tickLine={resolveVizAxisTickLineProps()}
                        axisLine={resolveVizAxisLineProps()}
                        tickMargin={axisStyle.tickMargin}
                        width={axisStyle.yAxisWidth}
                        tick={{ fontSize: axisStyle.tickFontSize }}
                      />
                    )}
                  </>
                )}
                {resolvedReferenceLines.map((line) => {
                  const axisProps = isVertical
                    ? useDualAxis
                      ? { x: line.value, xAxisId: "value" }
                      : { x: line.value }
                    : useDualAxis
                      ? { y: line.value, yAxisId: "left" }
                      : { y: line.value };

                  return (
                    <ReferenceLine
                      key={line.key}
                      {...axisProps}
                      {...resolveVizSemanticReferenceLineProps({
                        color: line.color,
                      })}
                      ifOverflow="extendDomain"
                      label={resolveVizReferenceLabelProps({
                        label: line.label,
                        color: line.color,
                      })}
                    />
                  );
                })}
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelKey={categoryKey}
                      labelClassName={
                        resolvedTooltipMode === "compact"
                          ? "text-[11px]"
                          : undefined
                      }
                      nameKey="dataKey"
                      labelFormatter={formatCategoryTooltipLabel}
                      allowedKeys={effectiveSeriesList.map(
                        (entry) => entry.key,
                      )}
                      formatToPercentFields={percentKeys}
                      valueFormatter={formatTooltipValue}
                      isGrouped={resolvedStacking !== "stacked"}
                    />
                  }
                />
                {shouldShowLegend ? (
                  <ChartLegend
                    content={(props) => (
                      <AiChartLegendContent
                        className={legendLayout.className}
                        verticalAlign={props.verticalAlign}
                        payload={legendPayload}
                        visibleKeySet={visibleSeriesKeySet}
                        onToggleItem={
                          resolvedAllowSeriesToggle
                            ? toggleSeriesVisibility
                            : undefined
                        }
                      />
                    )}
                    verticalAlign="bottom"
                    height={legendLayout.height}
                    wrapperStyle={legendLayout.wrapperStyle}
                  />
                ) : null}
                {effectiveSeriesList.map((entry) => {
                  const resolvedColor =
                    chartConfig[entry.key]?.color ?? "var(--chart-1)";
                  const displayLabel = chartConfig[entry.key]?.label;

                  const dualAxisProps = useDualAxis
                    ? isVertical
                      ? {
                          xAxisId:
                            resolveSeriesAxisSide(entry.key) === "right"
                              ? "percent"
                              : "value",
                        }
                      : {
                          yAxisId: resolveSeriesAxisSide(entry.key),
                        }
                    : null;

                  return (
                    <Bar
                      key={entry.key}
                      dataKey={entry.key}
                      name={resolveDisplayLabel(
                        displayLabel,
                        entry.label,
                        entry.key,
                      )}
                      fill={resolvedColor}
                      isAnimationActive={false}
                      animationBegin={0}
                      animationDuration={0}
                      stackId={stackId}
                      barSize={barSizing.barSize}
                      shape={renderSquareZeroSafeBarShape}
                      stroke="hsl(var(--background))"
                      strokeOpacity={barSeparatorStyle.strokeOpacity}
                      strokeWidth={barSeparatorStyle.strokeWidth}
                      {...(dualAxisProps ?? {})}
                    />
                  );
                })}
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
